import type {ReactElement} from 'react';
import type {Component, Editor} from 'grapesjs';
import type {EmailMakerControlProps} from '../../right-panel-registry.types';

export interface ExtendedControlIconSet {
  first: string;
  second: string;
  third: string;
  fourth: string;
}

export interface SliderControlDescriptor {
  property: string;
  labelKey: string;
  unit: string;
  min: number;
  max: number;
  step?: number;
  defaults?: string | number;
  isResponsive?: boolean;
  extendedControls?: boolean;
  extendedControlIcons?: ExtendedControlIconSet;
  toggleProp?: string;
}

export interface ExtendedControlValues {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface StepButtonProps {
  direction: 'plus' | 'minus';
  disabled: boolean;
  onClick: () => void;
}

export interface SliderTextInputProps {
  value: number | 'mixed';
  unit: string;
  min: number;
  max: number;
  step: number;
  centerAlign: boolean;
  isActive: boolean;
  propertyName: string;
  onActivate: () => void;
  onDeactivate: () => void;
  onChange: (val: number) => void;
}

export interface ExtendedControlInputProps {
  ariaLabel: string;
  iconClass: string;
  inputClassName?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  isSelected: boolean;
  onFocus: () => void;
  onBlur: () => void;
  onChange: (val: number) => void;
}

export interface SliderBodyProps {
  descriptor: SliderControlDescriptor;
  numericValue: number;
  displayValue: number | 'mixed';
  effectiveMax: number;
  isInputActive: boolean;
  renderExtraButton?: () => ReactElement | null;
  inputOptionsClassName?: string;
  onActivate: () => void;
  onDeactivate: () => void;
  onStep: (direction: 1 | -1) => void;
  onTextChange: (val: number) => void;
}

export interface SliderContainerProps {
  descriptor: SliderControlDescriptor;
  effectiveMax: number;
  component: Component;
  responsiveTag?: string;
  activeProperty: string;
  isHidden?: boolean;
}

export interface ResponsiveVariantSliderProps {
  descriptor: SliderControlDescriptor;
  effectiveMax: number;
  component: Component;
  activeProperty: string;
  responsiveTag: string;
  isHidden: boolean;
  editor: Editor | null;
}

export interface ResponsiveSliderControlProps extends EmailMakerControlProps {
  descriptor: SliderControlDescriptor;
  effectiveMax: number;
}

export interface SliderControlInnerProps extends EmailMakerControlProps {
  descriptor: SliderControlDescriptor;
}

export const DEFAULT_EXTENDED_CONTROL_ICONS: ExtendedControlIconSet = {
  first: 'icon-edge-spacing-top',
  second: 'icon-edge-spacing-bottom',
  third: 'icon-edge-spacing-left',
  fourth: 'icon-edge-spacing-right',
};

export const SLIDER_CSS_PROPS = {
  WIDTH: 'width',
  BORDER_RADIUS: 'border-radius',
  MARGIN: 'margin',
  PADDING: 'padding',
  PADDING_TOP: 'padding-top',
  PADDING_RIGHT: 'padding-right',
  PADDING_BOTTOM: 'padding-bottom',
  PADDING_LEFT: 'padding-left',
  BORDER_WIDTH: 'border-width',
  PMW_CELL_SPACING: 'pmw-cell-spacing',
  PMW_CELL_BORDER_WIDTH: 'pmw-cell-border-width',
  PMW_CELL_BORDER_RADIUS: 'pmw-cell-border-radius',
  PMW_ICON_MARGIN: 'pmw-icon-margin',
  PMW_ICON_SIZE: 'pmw-icon-size',
  PMW_COUNTDOWN_LABEL_SIZE: 'pmw-countdown-label-size',
} as const;

export const SLIDER_HIDE_INPUT_PROPS = new Set<string>([SLIDER_CSS_PROPS.PADDING, SLIDER_CSS_PROPS.BORDER_RADIUS, SLIDER_CSS_PROPS.MARGIN]);

// Driven from the slider write path so it never fires on template load.
export const SPACING_HIGHLIGHT_PROPS = new Set<string>([
  SLIDER_CSS_PROPS.PADDING,
  SLIDER_CSS_PROPS.PADDING_TOP,
  SLIDER_CSS_PROPS.PADDING_RIGHT,
  SLIDER_CSS_PROPS.PADDING_BOTTOM,
  SLIDER_CSS_PROPS.PADDING_LEFT,
  SLIDER_CSS_PROPS.PMW_CELL_SPACING,
]);

// Literal hex because the fill percentage is computed at runtime.
export const RANGE_FILL_COLOR = '#3FBCE7';
export const RANGE_TRACK_COLOR = '#E6E6EC';
